/**
 * KioskCheckIn — white, airy kiosk-native QR check-in.
 * Reuses the proven webcam + jsQR scan loop from QRScannerGun, but with
 * kiosk behaviour: results stay on screen until staff dismisses them, the
 * ticket card shows add-ons to fulfil + payment status, and recent
 * check-ins below the camera are clickable to reopen their ticket.
 */
import React, { useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import jsQR from 'jsqr';
import { Camera, CheckCircle2, AlertTriangle, XCircle, Users, CalendarDays, Package, CreditCard, Ticket } from 'lucide-react';
import api from '../utils/api';
import { hapticSuccess, hapticError } from '../utils/haptics';

const PayBadge = ({ status }) => {
  const paid = (status || '').toLowerCase() === 'paid';
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${
        paid ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
      }`}
      data-testid="kiosk-checkin-pay-badge"
    >
      <CreditCard className="w-3.5 h-3.5" />
      {paid ? 'Paid' : (status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Payment pending')}
    </span>
  );
};

const KioskCheckIn = ({ scannerToken, brand = '#2563EB', checkIns = [], onRefresh }) => {
  const webcamRef = useRef(null);
  const busyRef = useRef(false);
  const [cameraError, setCameraError] = useState('');
  const [result, setResult] = useState(null); // {status: success|already|error|detail, booking, error}

  useEffect(() => {
    const tick = () => {
      if (busyRef.current || !webcamRef.current) return;
      const shot = webcamRef.current.getScreenshot();
      if (!shot) return;
      const img = new Image();
      img.onload = () => {
        if (busyRef.current) return;
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(data.data, data.width, data.height, { inversionAttempts: 'dontInvert' });
        if (code?.data) handleCode(code.data);
      };
      img.src = shot;
    };
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, []);

  const handleCode = async (qrData) => {
    if (busyRef.current) return;
    busyRef.current = true;
    let code = qrData;
    if (qrData.includes('/check-in/')) code = qrData.split('/check-in/')[1].split('?')[0];
    else if (qrData.includes('confirmation=')) code = qrData.split('confirmation=')[1].split('&')[0];
    if (navigator.vibrate) navigator.vibrate(150);
    try {
      const r = await api.post('/v2/public/check-in/scan', { confirmationCode: code, scannerToken });
      setResult({ status: r.data?.alreadyCheckedIn ? 'already' : 'success', booking: r.data?.booking });
      hapticSuccess();
      if (onRefresh) onRefresh();
    } catch (e) {
      const d = e.response?.data?.detail;
      setResult({ status: 'error', error: typeof d === 'string' ? d : 'Check-in failed — try again', code });
      hapticError();
    }
  };

  const openDetail = (c) => {
    busyRef.current = true;
    setResult({ status: 'detail', booking: c });
  };

  const dismiss = () => {
    setResult(null);
    busyRef.current = false;
  };

  const b = result?.booking;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="kiosk-checkin-view">
      {/* Camera */}
      <div className="relative rounded-3xl overflow-hidden bg-gray-950 shadow-[0_12px_40px_rgba(16,24,40,0.12)]">
        <Webcam
          ref={webcamRef}
          audio={false}
          playsInline
          screenshotFormat="image/jpeg"
          videoConstraints={{ facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }}
          onUserMedia={() => setCameraError('')}
          onUserMediaError={() => setCameraError('Camera access is blocked. Allow camera permission for this site, then try again.')}
          className="w-full h-auto"
          style={{ minHeight: 320, maxHeight: '52vh', objectFit: 'cover' }}
        />

        {/* scanning frame */}
        {!result && !cameraError && (
          <>
            <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white/15 text-white backdrop-blur-md flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
              Scanning
            </div>
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="relative w-52 h-52 text-white/70">
                <div className="absolute top-0 left-0 w-7 h-7 border-t border-l border-current rounded-tl-xl" />
                <div className="absolute top-0 right-0 w-7 h-7 border-t border-r border-current rounded-tr-xl" />
                <div className="absolute bottom-0 left-0 w-7 h-7 border-b border-l border-current rounded-bl-xl" />
                <div className="absolute bottom-0 right-0 w-7 h-7 border-b border-r border-current rounded-br-xl" />
              </div>
            </div>
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-4 pt-10">
              <p className="text-white/80 text-center text-xs sm:text-sm">Point the camera at the QR code on the guest&apos;s ticket</p>
            </div>
          </>
        )}

        {/* camera blocked */}
        {cameraError && !result && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-950/95 px-6 text-center">
            <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-4">
              <Camera className="w-7 h-7 text-white/70" />
            </div>
            <p className="text-sm text-white/80 max-w-xs mb-5">{cameraError}</p>
            <button
              onClick={() => { setCameraError(''); window.location.reload(); }}
              className="px-5 py-2.5 text-white text-sm font-semibold rounded-full"
              style={{ backgroundColor: brand }}
              data-testid="kiosk-camera-retry"
            >
              Try again
            </button>
          </div>
        )}

        {/* result / ticket card — stays until dismissed */}
        {result && (
          <div className="absolute inset-0 bg-white/[0.97] backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="kiosk-checkin-result">
            <div className="w-full max-w-sm text-center py-2">
              {result.status === 'error' ? (
                <>
                  <XCircle className="w-14 h-14 text-red-500 mx-auto mb-3" />
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Couldn&apos;t check in</h3>
                  <p className="text-sm text-gray-600 mb-1">{result.error}</p>
                  {result.code && <p className="text-xs font-mono text-gray-400 mb-4">{String(result.code).substring(0, 24)}</p>}
                </>
              ) : (
                <>
                  {result.status === 'already' ? (
                    <AlertTriangle className="w-14 h-14 text-amber-500 mx-auto mb-3" />
                  ) : (
                    <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
                  )}
                  <h3 className="text-xl font-bold text-gray-900 mb-0.5">
                    {result.status === 'already' ? 'Already checked in' : result.status === 'detail' ? 'Checked in' : 'Checked in!'}
                  </h3>
                  <p className="text-lg font-semibold text-gray-800" data-testid="kiosk-checkin-name">{b?.customerName}</p>
                  {b?.serviceName && <p className="text-sm text-gray-500 mt-0.5">{b.serviceName}</p>}

                  <div className="flex items-center justify-center gap-3 mt-3 flex-wrap text-xs text-gray-600">
                    {b?.confirmationCode && (
                      <span className="inline-flex items-center gap-1"><Ticket className="w-3.5 h-3.5 text-gray-400" /><span className="font-mono">{b.confirmationCode}</span></span>
                    )}
                    {b?.date && (
                      <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5 text-gray-400" />{b.date}{b?.time ? ` · ${b.time}` : ''}</span>
                    )}
                    {b?.partySize && (
                      <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5 text-gray-400" />{b.partySize} {b.partySize === 1 ? 'guest' : 'guests'}</span>
                    )}
                  </div>

                  <div className="mt-3 flex justify-center"><PayBadge status={b?.paymentStatus} /></div>

                  {b?.addOns?.length > 0 && (
                    <div className="mt-4 bg-amber-50 rounded-2xl p-3 text-left" data-testid="kiosk-checkin-addons">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700 flex items-center gap-1.5 mb-1.5">
                        <Package className="w-3.5 h-3.5" /> Add-ons to provide
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {b.addOns.map((a, i) => (
                          <span key={i} className="text-xs bg-white text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full">{a}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
              <button
                onClick={dismiss}
                className="mt-5 w-full py-3 rounded-full text-white text-sm font-semibold shadow-sm"
                style={{ backgroundColor: brand }}
                data-testid="kiosk-checkin-dismiss"
              >
                {result.status === 'error' ? 'Scan again' : 'Done — next guest'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Recent check-ins — tap a row to reopen the ticket */}
      {checkIns.length > 0 && (
        <div className="bg-white rounded-3xl shadow-[0_4px_20px_rgba(16,24,40,0.06)] p-4 sm:p-5" data-testid="kiosk-recent-checkins">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">Recent check-ins</p>
          <div className="space-y-1">
            {checkIns.map((c) => (
              <button
                key={c.id}
                onClick={() => openDetail(c)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl hover:bg-gray-50 transition-colors text-left"
                data-testid="kiosk-recent-checkin-row"
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {c.maskedName} <span className="font-mono text-xs text-gray-400">{c.confirmationCode}</span>
                  </p>
                  {c.serviceName && <p className="text-xs text-gray-500 truncate">{c.serviceName}</p>}
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {c.minutesAgo === null || c.minutesAgo === undefined ? '' : c.minutesAgo < 1 ? 'just now' : `${c.minutesAgo}m ago`}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default KioskCheckIn;
